import { getLogger } from 'wanderu-logger';
import { areCookiesAvailable, isSessionStorageAvailable } from 'wanderu-utils';

import type { Cookies } from 'react-cookie';

const logger = getLogger('affiliate-analytics');

const AFFILIATE_STORAGE_KEY = 'affiliate_data';
const AFFILIATE_ID_COOKIE_NAME = 'aid';
const TRACKING_ID_COOKIE_NAME = 'tid';
const COOKIE_OPTIONS = {
    path: '/',
    sameSite: true,
    secure: true,
};

export interface AffiliateData {
    affiliateId?: string;
    trackingId?: string;
}

const getAffiliateDataFromCookies = (cookies: Cookies): AffiliateData => {
    const affiliateId = cookies.get(AFFILIATE_ID_COOKIE_NAME);
    const trackingId = cookies.get(TRACKING_ID_COOKIE_NAME);

    return {
        affiliateId,
        trackingId,
    };
};

const setAffiliateDataCookies = (cookies: Cookies, {
    affiliateId,
    trackingId,
}: AffiliateData) => {
    if (!areCookiesAvailable()) {
        return;
    }

    if (affiliateId) {
        cookies.set(AFFILIATE_ID_COOKIE_NAME, affiliateId, COOKIE_OPTIONS);
    }

    if (trackingId) {
        cookies.set(TRACKING_ID_COOKIE_NAME, trackingId, COOKIE_OPTIONS);
    }
};

const clearAffiliateDataCookies = (cookies: Cookies) => {
    cookies.remove(AFFILIATE_ID_COOKIE_NAME, COOKIE_OPTIONS);
    cookies.remove(TRACKING_ID_COOKIE_NAME, COOKIE_OPTIONS);
};

const getAffiliateDataFromSessionStorage = (): AffiliateData => {
    if (!isSessionStorageAvailable()) {
        return {};
    }

    const affiliateData = window.sessionStorage.getItem(AFFILIATE_STORAGE_KEY);

    if (!affiliateData) {
        return {};
    }

    try {
        const data = JSON.parse(affiliateData);
        return {
            affiliateId: data?.affiliateId,
            trackingId: data?.trackingId,
        };
    } catch (e) {
        logger.warn('Unable to parse session storage value for affiliate data', e);
        return {};
    }
};

const setAffiliateDataInSessionStorage = (data: AffiliateData) => {
    if (!isSessionStorageAvailable()) {
        return undefined;
    }

    return window.sessionStorage.setItem(
        AFFILIATE_STORAGE_KEY,
        JSON.stringify(data),
    );
};

const initAffiliateData = (cookies: Cookies): AffiliateData => {
    if (!areCookiesAvailable()) {
        return getAffiliateDataFromSessionStorage();
    }

    const { affiliateId, trackingId } = getAffiliateDataFromCookies(cookies);
    // NB: We want to remove the cookies as soon as we read them as, in some cases, session cookies
    // last until the browser is closed. Session storage however, is cleared when the tab is closed.
    clearAffiliateDataCookies(cookies);

    if (affiliateId) {
        setAffiliateDataInSessionStorage({
            affiliateId,
            trackingId,
        });

        return {
            affiliateId,
            trackingId,
        };
    }

    return getAffiliateDataFromSessionStorage();
};

export {
    getAffiliateDataFromCookies,
    initAffiliateData,
    setAffiliateDataCookies,
};
